import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '../UI/TextField/TextField';
import { useTranslation } from 'react-i18next';
import FormControlLabel from '@mui/material/FormControlLabel';
import RichTextField from '../UI/RichTextField/RichTextField';
import SubSectionHeading from '../UI/Typography/SubSectionHeading';
import isEmpty from '../../is-empty';
import SelectField from '../UI/SelectField/SelectField';
import Switch from '../UI/Switch/Switch';
import Button from '../UI/Buttons/Button';

/**
 * HeadersFootersForm - Embedded form for editing Headers & Footers
 *
 * @param {string} recordId - ID of the record to edit (null for new)
 * @param {function} onSave - Callback after successful save
 * @param {function} onCancel - Callback to cancel/go back
 * @param {function} onDelete - Callback after successful delete
 */
const HeadersFootersForm = ({ recordId, onSave, onCancel, onDelete }) => {
    const auth = useSelector(state => state.auth);
    const permission = useSelector(state => state.security?.currentPath);
    const errors = useSelector(state => state.errors.list);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [state, setState] = useState({
        _id: recordId || '',
        header: '',
        footer: '',
        name: '',
        global: false,
        parameters: [],
        parametersSel: [],
        parameterOptions: [],
        security: permission,
        enableSave: true,
    });

    useEffect(() => {
        const fetchData = async () => {
            const values = await axios.get('/api/headerandfooter/values');
            setState((prevState) => ({
                ...prevState,
                parameterOptions: values.data.parameters
            }));
        };
        fetchData();
    }, [auth, permission]);

    const loadRecord = useCallback(async () => {
        if (!state._id) return;
        const record = await axios.get(`/api/headerandfooter/get/${state._id}`);
        let parametersSel = [];
        if (record.data.parameters != null && record.data.parameters.length > 0) {
            record.data.parameters.forEach((row) => {
                let val = state.parameterOptions.find((x) => x.value === row);
                if (val) parametersSel.push(val);
            });
        }
        setState((prevState) => ({
            ...prevState,
            header: record.data.header,
            footer: record.data.footer,
            name: record.data.name,
            global: record.data.global,
            parameters: record.data.parameters,
            parametersSel: parametersSel,
        }));
    }, [state._id, state.parameterOptions]);

    useEffect(() => {
        if (state._id && state.parameterOptions.length > 0) {
            loadRecord();
        }
    }, [state._id, state.parameterOptions.length, loadRecord]);

    const changeBool = (name) => {
        setState((prevState) => ({ ...prevState, [name]: !prevState[name] }));
    };

    const changeRichText = (type) => (index, content) => {
        setState((prevState) => ({ ...prevState, [type]: content }));
    };

    const changeValue = (e) => {
        setState((prevState) => ({ ...prevState, [e.target.name]: e.target.value }));
    };

    const changeMulti = (name, data, optName) => (e) => {
        let options = state[optName];
        let selectedValues = e.target.value;
        if (selectedValues.includes('all')) {
            let currentSels = state[name];
            if (options.length !== currentSels.length) {
                let newSels = [...options];
                let newIds = options.map(x => x.value);
                setState((prevState) => ({ ...prevState, [name]: newSels, [data]: newIds }));
            } else {
                setState((prevState) => ({ ...prevState, [name]: [], [data]: [] }));
            }
        } else {
            let newSels = selectedValues.map(id => options.find(x => x.value === id)).filter(Boolean);
            let newIds = newSels.map(x => x.value);
            setState((prevState) => ({ ...prevState, [name]: newSels, [data]: newIds }));
        }
    };

    const deleteMulti = (name, data) => (e, value) => {
        let sels = [...state[name]];
        let ids = [...state[data]];
        let index = sels.findIndex((x) => x.value === value.value);
        if (index > -1) {
            sels.splice(index, 1);
            ids.splice(index, 1);
        }
        setState((prevState) => ({ ...prevState, [name]: sels, [data]: ids }));
    };

    const validateParameters = (value) => {
        let parametersSel = state.parametersSel;
        let errors = [];
        let more = true;
        let startPosition = 0;
        while (more) {
            let firstIndex = value.indexOf('%%', startPosition);
            if (firstIndex > -1) {
                let secondIndex = value.indexOf('%%', firstIndex + 2);
                if (secondIndex > -1) {
                    let parm = value.substring(firstIndex + 2, secondIndex);
                    let foundName = parametersSel.find((x) => x?.label?.toUpperCase().replace(/\s+/g, '') === parm);
                    if (foundName == null) {
                        errors.push(parm + ' ' + t('notValid'));
                    }
                    startPosition = secondIndex + 2;
                } else {
                    more = false;
                }
            } else {
                more = false;
            }
        }
        return errors;
    };

    const handleSave = async () => {
        if (!state.enableSave) return;
        setState((prevState) => ({ ...prevState, enableSave: false }));

        let validationErrors = {};
        if (state.name == null || state.name === '') validationErrors[t('name')] = t('required');
        if (state.parameters == null || state.parameters.length === 0) validationErrors[t('parameters')] = t('required');
        let headerErrors = validateParameters(state.header);
        if (headerErrors.length > 0) validationErrors[t('header')] = headerErrors;
        let footerErrors = validateParameters(state.footer);
        if (footerErrors.length > 0) validationErrors[t('footer')] = footerErrors;

        if (!isEmpty(validationErrors)) {
            dispatch({ type: 'CREATE_ERROR', payload: validationErrors });
            setState((prevState) => ({ ...prevState, enableSave: true }));
            return;
        }

        let data = {
            _id: state._id,
            name: state.name,
            parameters: state.parameters,
            header: state.header,
            footer: state.footer,
            global: state.global,
        };

        try {
            let result = await axios.post('/api/headerandfooter/save', data);
            setState((prevState) => ({ ...prevState, _id: result.data.id, enableSave: true }));
            dispatch({ type: 'CREATE_ALERT', payload: { message: result.data.message, title: t('success'), severity: 'success' } });
            dispatch({ type: 'CLEAR_ERROR' });
            if (onSave) onSave();
        } catch (err) {
            setState((prevState) => ({ ...prevState, enableSave: true }));
            dispatch({ type: 'CREATE_ALERT', payload: { message: err.response?.data || t('error'), title: t('error'), severity: 'error' } });
        }
    };

    const handleCopy = async () => {
        if (!state.enableSave || !state._id) return;
        setState((prevState) => ({ ...prevState, enableSave: false }));

        try {
            let result = await axios.post('/api/headerandfooter/copy', { _id: state._id });
            setState((prevState) => ({ ...prevState, _id: result.data.id, enableSave: true }));
            dispatch({ type: 'CREATE_ALERT', payload: { message: result.data.message, title: t('success'), severity: 'success' } });
            loadRecord();
        } catch (err) {
            setState((prevState) => ({ ...prevState, enableSave: true }));
            dispatch({ type: 'CREATE_ALERT', payload: { message: err.response?.data || t('error'), title: t('error'), severity: 'error' } });
        }
    };

    const handleDelete = async () => {
        if (!state.enableSave || !state._id) return;
        setState((prevState) => ({ ...prevState, enableSave: false }));

        try {
            let result = await axios.delete(`/api/headerandfooter/delete/${state._id}`);
            dispatch({ type: 'CREATE_ALERT', payload: { message: result.data.message, title: t('success'), severity: 'success' } });
            if (onDelete) onDelete();
        } catch (err) {
            setState((prevState) => ({ ...prevState, enableSave: true }));
            dispatch({ type: 'CREATE_ALERT', payload: { message: err.response?.data || t('error'), title: t('error'), severity: 'error' } });
        }
    };

    const { global, security } = state;
    const fieldAccess = global === true && auth.user.internal === true ? true :
        global === true && auth.user.internal === false ? false :
            global === false && security?.update === true ? true : false;

    const canSave = global === true && auth.user.internal === true ? true :
        global === true && auth.user.internal === false ? false :
            global === false && state._id && security?.update === true ? true :
                !state._id && security?.create === true ? true : false;

    return (
        <Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                    <TextField
                        value={state.name}
                        onChange={changeValue}
                        name="name"
                        size="small"
                        fullWidth
                        disabled={!fieldAccess}
                        label={t('name')}
                        data-cy='name'
                        error={errors[t('name')] != null}
                        helperText={errors[t('name')]}
                    />
                </Grid>
                <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={global}
                                onChange={() => changeBool('global')}
                                name="global"
                                disabled={!fieldAccess}
                                data-cy='global'
                            />
                        }
                        label={t('global')}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={12}>
                    <SelectField
                        disabled={!fieldAccess}
                        selectAll
                        value={state.parameters || []}
                        valueObjects={state.parametersSel}
                        options={state.parameterOptions}
                        label={t('parameters')}
                        selectAllLabel={t('selectAll')}
                        error={errors[t('parameters')] != null}
                        helperText={errors[t('parameters')]}
                        onChange={changeMulti('parametersSel', 'parameters', 'parameterOptions')}
                        onDelete={deleteMulti('parametersSel', 'parameters')}
                        data-cy='parameters'
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={12}>
                    <SubSectionHeading title={t('header')} />
                    <Box sx={{ mt: 1 }}>
                        <RichTextField
                            disabled={!fieldAccess}
                            id={'headerFieldEmbed'}
                            key={'headerFieldEmbed'}
                            index={1}
                            value={state.header != null ? state.header : ''}
                            errors={errors[t('header')]}
                            height={180}
                            changeRichText={changeRichText('header')}
                            data-cy='header'
                        />
                    </Box>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={12}>
                    <SubSectionHeading title={t('footer')} />
                    <Box sx={{ mt: 1 }}>
                        <RichTextField
                            disabled={!fieldAccess}
                            id={'footerFieldEmbed'}
                            key={'footerFieldEmbed'}
                            index={1}
                            value={state.footer != null ? state.footer : ''}
                            errors={errors[t('footer')]}
                            height={180}
                            changeRichText={changeRichText('footer')}
                            data-cy='footer'
                        />
                    </Box>
                </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                {canSave && (
                    <Button variant='contained' onClick={handleSave} disabled={!state.enableSave} data-cy='save'>
                        {t('save')}
                    </Button>
                )}
                {state._id && (
                    <Button variant='outlined' onClick={handleCopy} disabled={!state.enableSave} data-cy="copy">
                        {t('copy')}
                    </Button>
                )}
                {global === false && state._id && security?.delete && (
                    <Button variant='outlined' color="error" onClick={handleDelete} disabled={!state.enableSave} data-cy='delete'>
                        {t('delete')}
                    </Button>
                )}
                <Button variant='outlined' onClick={onCancel}>
                    {t('cancel')}
                </Button>
            </Stack>
        </Box>
    );
};

export default HeadersFootersForm;
